import type { Documento } from '@/services/Documentos';

export type AbaDaLista = 'entradas' | 'saidas';

export interface ColunaDaLista {
  titulo: string;
  alinhamento: 'esquerda' | 'direita' | 'centro';
}

export const CLASSE_DO_ALINHAMENTO: Record<ColunaDaLista['alinhamento'], string> = {
  esquerda: '',
  direita: 'text-right',
  centro: 'text-center',
};

/**
 * As colunas da lista de Documentos Fiscais, na ordem da SPEC-018, §1, decisão 4: a nota, o participante, tipo e
 * finalidade, a situação, o valor, IBS UF, IBS Mun, IBS total e CBS; nas Entradas, o crédito disponível. A tabela e o
 * esqueleto do carregamento leem daqui, para não divergirem.
 */
export function colunasDaLista(aba: AbaDaLista): ColunaDaLista[] {
  return [
    { titulo: 'CNPJ Contribuinte', alinhamento: 'esquerda' },
    { titulo: 'Número', alinhamento: 'esquerda' },
    { titulo: 'Data', alinhamento: 'esquerda' },
    // A data de entrada física da mercadoria (SPEC-018, decisão 12), só nas Entradas, ao lado da data de emissão.
    ...(aba === 'entradas'
      ? [{ titulo: 'Entrada da mercadoria', alinhamento: 'esquerda' as const }]
      : []),
    { titulo: aba === 'entradas' ? 'Fornecedor' : 'Cliente', alinhamento: 'esquerda' },
    { titulo: 'CNPJ', alinhamento: 'esquerda' },
    { titulo: 'Tipo e finalidade', alinhamento: 'esquerda' },
    { titulo: 'Situação', alinhamento: 'esquerda' },
    { titulo: 'Valor Total', alinhamento: 'direita' },
    { titulo: 'IBS UF', alinhamento: 'direita' },
    { titulo: 'IBS Mun', alinhamento: 'direita' },
    { titulo: 'IBS total', alinhamento: 'direita' },
    { titulo: 'CBS', alinhamento: 'direita' },
    ...(aba === 'entradas'
      ? [{ titulo: 'Crédito disponível', alinhamento: 'direita' as const }]
      : []),
    { titulo: 'Créd.Pres.', alinhamento: 'direita' },
    { titulo: 'Ações', alinhamento: 'centro' },
  ];
}

/** O valor que o servidor manda em texto ("12.34"); vazio ou inválido vale zero. */
export function valorDaNota(valor: string | null | undefined): number {
  return parseFloat(valor ?? '') || 0;
}

const SITUACAO_DA_NOTA: Record<string, string> = {
  Homologado: 'Autorizada',
  Cancelado: 'Cancelada',
  Denegado: 'Denegada',
  Divergencia: 'Divergência',
};

/**
 * A situação da nota (SPEC-018, §1, decisão 4), no singular das situações que o topo de cada modelo conta
 * (autorizadas, canceladas, denegadas, divergências; SPEC-004). Vem do `StatusDescricao` da linha.
 */
export function situacaoDaNota(doc: { StatusDescricao?: string | null }): string {
  const descricao = doc.StatusDescricao?.trim() ?? '';
  if (!descricao) return 'Sem situação';
  return SITUACAO_DA_NOTA[descricao] ?? descricao;
}

/**
 * As marcas da coluna "Tipo e finalidade" (SPEC-018, §3, item 4). Por ora, "emissão própria": a NF-e de entrada que a
 * empresa emitiu (SPEC-007, decisão 3). As de nota de crédito e de débito e a "fora do padrão" dependem da SPEC-010.
 */
export function marcasDaNota(doc: Pick<Documento, 'EmissaoPropria'>): string[] {
  return doc.EmissaoPropria ? ['emissão própria'] : [];
}

export interface CreditoDisponivel {
  ibs: number;
  cbs: number;
}

const temValor = (valor: string | null | undefined) => valor != null && valor.trim() !== '';

/**
 * O crédito disponível da NF-e de entrada liberada (SPEC-018, §3, item 4; SPEC-002, §4, item 2): o de IBS e o de CBS,
 * cada um a soma dos itens. `null` quando a nota não tem crédito calculado: o servidor só o calcula para a nota que o
 * Recebimento liberou.
 */
export function creditoDisponivelDaNota(
  doc: Pick<Documento, 'CreditoDisponivelIbs' | 'CreditoDisponivelCbs'>
): CreditoDisponivel | null {
  if (!temValor(doc.CreditoDisponivelIbs) && !temValor(doc.CreditoDisponivelCbs)) return null;
  return {
    ibs: valorDaNota(doc.CreditoDisponivelIbs),
    cbs: valorDaNota(doc.CreditoDisponivelCbs),
  };
}
